import { useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../../components/confirm';
import './preLoginSettings.css';
import type { PreLoginSchedule, PreLoginScheduleWriteInput } from './types';

/** The COC facts the XAF "Task Recurrance Setup" form shows read-only. */
export interface ScheduleCocInfo {
  cocId: string | null;
  clientName: string | null;
  clientAddress: string | null;
  projectCode: string | null;
  projectName: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  cocOid: string;
  info: ScheduleCocInfo;
  rows: PreLoginSchedule[];
  loading: boolean;
  onRefresh: () => void;
}

const FORM = 'Pre-Login Settings';

/** DevExpress WeekDays bit flags, Sunday-first — matches
 *  `PreLoginRecurrencePattern` / the legacy payload exactly. */
const WEEKDAYS: { flag: number; label: string }[] = [
  { flag: 1,  label: 'Su' }, { flag: 2,  label: 'Mo' }, { flag: 4,  label: 'Tu' },
  { flag: 8,  label: 'We' }, { flag: 16, label: 'Th' }, { flag: 32, label: 'Fr' },
  { flag: 64, label: 'Sa' },
];

/** Legacy replaces DevExpress's "Last" with "Fifth" (Editor_RecurrencePopupControlCreated,
 *  TaskRecurranceSetupController :312/:330: Remove(Last), Add("Fifth", 5)) — the
 *  SAME numeric value 5, so the stored payload is identical. */
const WEEK_OF_MONTH = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The recurrence as the "…" popup edits it — 1:1 with `PreLoginRecurrencePattern`. */
interface Rec {
  type: number;             // 0 Daily · 1 Weekly · 2 Monthly · 3 Yearly
  periodicity: number;
  weekDays: number;         // Weekly: bit flags
  monthlyMode: 'day' | 'weekday';
  dayNumber: number;
  weekOfMonth: number;      // 1..5
  onWeekday: number;        // a single WEEKDAYS flag
  month: number;            // 1..12 (Yearly)
  range: 0 | 1 | 2;         // 0 not chosen yet · 1 end after N occurrences · 2 end by date
  occurrenceCount: number;
  recurrenceEnd: string;    // yyyy-MM-dd
}

/** Legacy hides the "No end date" radio (RangeCtl.DeNoEnd.ClientVisible = false,
 *  :290), so a schedule always ends: after N occurrences or by a date. Neither
 *  radio starts selected in the XAF popup, and OK asks for one. */
const FRESH: Rec = {
  type: 0, periodicity: 1, weekDays: 2, monthlyMode: 'day', dayNumber: 1,
  weekOfMonth: 1, onWeekday: 2, month: 1, range: 0, occurrenceCount: 1, recurrenceEnd: '',
};

const fromRow = (r: PreLoginSchedule): Rec | null => {
  if (r.noRecurrence) return null;
  return {
    type: r.recurType ?? 0,
    periodicity: r.recurPeriodicity || 1,
    weekDays: r.recurWeekDays || 2,
    monthlyMode: (r.recurWeekOfMonth ?? 0) > 0 ? 'weekday' : 'day',
    dayNumber: r.recurDayNumber || 1,
    weekOfMonth: r.recurWeekOfMonth || 1,
    onWeekday: r.recurWeekDays && r.recurWeekOfMonth ? r.recurWeekDays : 2,
    month: r.recurMonth || 1,
    // a stored "no end" (0) has no radio here, so it opens with none chosen
    range: r.recurRange === 1 ? 1 : r.recurRange === 2 ? 2 : 0,
    occurrenceCount: r.occurrenceCount || 1,
    recurrenceEnd: r.seriesEndsOn?.slice(0, 10) ?? '',
  };
};

/** One sentence for the read-only Recurrence box (the server words saved rows;
 *  this words an edit not yet saved). */
const describe = (r: Rec): string => {
  const unit = ['day', 'week', 'month', 'year'][r.type];
  const every = r.periodicity > 1 ? `Every ${r.periodicity} ${unit}s` : `Every ${unit}`;
  let on = '';
  if (r.type === 1) {
    const names = WEEKDAYS.filter(d => (r.weekDays & d.flag) !== 0).map(d => d.label).join(', ');
    on = names ? ` on ${names}` : '';
  } else if (r.type >= 2) {
    const where = r.monthlyMode === 'day'
      ? `day ${r.dayNumber}`
      : `the ${(WEEK_OF_MONTH[r.weekOfMonth - 1] ?? "").toLowerCase()} ${WEEKDAYS.find(d => d.flag === r.onWeekday)?.label ?? ''}`;
    on = ` on ${where}${r.type === 3 ? ` of ${MONTHS[r.month - 1]}` : ''}`;
  }
  const end = r.range === 1 ? `, ${r.occurrenceCount} occurrence${r.occurrenceCount === 1 ? '' : 's'}`
            : r.range === 2 && r.recurrenceEnd ? `, until ${r.recurrenceEnd}` : '';
  return `${every}${on}${end}`;
};

/**
 * **Scheduled Task Tracking** — the recurring schedule(s) that drive when this
 * COC's samples are due. Laid out exactly like the XAF popups (owner request
 * 2026-09-29, screenshots of the legacy windows):
 *
 * <ul>
 *  <li>The LIST popup is titled "Scheduled Task Tracking": a New button and a
 *      grid of just Recurrence and Priority; opening a row edits it.</li>
 *  <li>New / Edit opens "Task Recurrance Setup": read-only COC ID, Client Name,
 *      Client Address, Project Code, Project Name; then Start On, Recurrence (a
 *      read-only box with a "…" button that opens the recurrence editor),
 *      Priority (default Primary) and Remark; Save and Cancel.</li>
 * </ul>
 *
 * <p>No Task-name / End / Description fields: legacy's `NewRecurrence_Execute`
 * (TaskRecurranceSetupController :179) sets `Subject = ProjectID.ProjectCode`,
 * `StartOn = today` and `EndOn = end of that day` itself, so the form never
 * asks. The same is done here.</p>
 *
 * <p>The recurrence XML is built entirely SERVER-SIDE by
 * `PreLoginRecurrencePattern` (proven lossless against all 105 live payloads);
 * the browser only sends structured fields.</p>
 */
export default function PreLoginScheduleDialog({ open, onClose, cocOid, info, rows, loading, onRefresh }: Props) {
  const runDelete = useDeleteFlow();
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [editing, setEditing] = useState<PreLoginSchedule | 'new' | null>(null);

  const columns: AppColumn<PreLoginSchedule>[] = useMemo(() => [
    { field: 'recurrenceText', header: 'Recurrence', width: 380 },
    { field: 'priority',       header: 'Priority',   width: 140 },
  ], []);

  const deleteSelected = () => {
    if (sel.size === 0) return;
    void runDelete({
      formName: FORM,
      message: `Delete ${sel.size} task schedule${sel.size === 1 ? '' : 's'}?`,
      delete: async () => {
        for (const oid of sel) {
          await api.delete(`/api/v1/pre-login-settings/${cocOid}/schedules/${String(oid)}`);
        }
      },
      onDeleted: () => { setSel(new Set()); onRefresh(); },
    });
  };

  return (
    <>
      <AppDialog
        open={open && !editing}
        onClose={onClose}
        title="Scheduled Task Tracking"
        icon="ti-calendar-repeat"
        width={640}
        footer={<button className="ca-btn" onClick={onClose}>Close</button>}
      >
        <div className="pl-sectionbar">
          <button className="ca-btn ca-btn-solid" onClick={() => setEditing('new')}>
            <i className="ti ti-plus" /> New
          </button>
          <button className="ca-btn" disabled={sel.size === 0} onClick={deleteSelected}>
            <i className="ti ti-trash" style={{ color: 'var(--ai-icon-danger, #c0392b)' }} /> Delete
          </button>
        </div>

        <AppDataGrid<PreLoginSchedule>
          rows={rows}
          columns={columns}
          getRowId={r => r.oid}
          loading={loading}
          selectable
          selectedIds={sel}
          onSelectionChange={setSel}
          onRowDoubleClick={row => setEditing(row)}
          // Legacy PageSize="10" on TaskRecurranceSetup_ListView_COCSettings.
          defaultPageSize={10}
          exportFilename="Pre-Login Task Schedule"
          emptyText="No data to display"
        />

        {/* Say WHY the neighbouring field is locked, rather than leaving the user
            to discover it by having their entry silently zeroed. */}
        {!loading && rows.length === 0 && (
          <p className="sr-note" style={{ marginTop: 8 }}>
            No schedule, so <b>#Days In Advance</b> cannot be set — it is a lead
            time before the next scheduled sampling, and there is nothing yet to
            lead. Legacy reports this as <i>"RecurranceSetup is not available."</i>
          </p>
        )}
      </AppDialog>

      {open && editing && (
        <ScheduleForm
          cocOid={cocOid}
          info={info}
          row={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </>
  );
}

interface FormProps {
  cocOid: string;
  info: ScheduleCocInfo;
  /** null = New. */
  row: PreLoginSchedule | null;
  onCancel: () => void;
  onSaved: () => void;
}

/** "Task Recurrance Setup" — the XAF TaskRecurranceSetup_DetailView. */
function ScheduleForm({ cocOid, info, row, onCancel, onSaved }: FormProps) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [startOn, setStartOn]   = useState(row?.startOn?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
  const [rec, setRec]           = useState<Rec | null>(row ? fromRow(row) : null);
  const [recText, setRecText]   = useState<string>(row?.recurrenceText ?? '');
  const [priority, setPriority] = useState<number>(row?.priorityCode ?? 0);   // Primary
  const [remark, setRemark]     = useState(row?.remark ?? '');
  const [recOpen, setRecOpen]   = useState(false);

  const submit = () => {
    if (!startOn) { notify.error("'Start On' must not be empty."); return; }
    // Legacy saves nothing without a recurrence (Save_Execute :358) and DELETES an
    // existing setup whose recurrence was cleared, so a schedule always has one.
    if (!rec) {
      if (row) {
        // Legacy Save_Execute :401 - an existing setup whose recurrence was
        // removed is DELETED on Save.
        void runDelete({
          formName: FORM,
          message: 'Removing the recurrence deletes this task schedule. Continue?',
          delete: async () => { await api.delete(`/api/v1/pre-login-settings/${cocOid}/schedules/${row.oid}`); },
          onDeleted: onSaved,
        });
        return;
      }
      notify.error("'Recurrence' must not be empty.");
      return;
    }

    const body: PreLoginScheduleWriteInput = {
      // Legacy sets Subject = ProjectID.ProjectCode itself; keep an existing name.
      taskName: (row?.taskName ?? info.projectCode ?? info.cocId ?? 'Task').trim() || 'Task',
      startOn,
      // Legacy: EndOn = 23:59 on the StartOn day.
      endOn: `${startOn}T23:59:00`,
      description: row?.description ?? null,
      remark: remark || null,
      priority,
      noRecurrence: false,
      type: rec.type,
      periodicity: rec.periodicity <= 0 ? 1 : rec.periodicity,
      // Blank whatever the chosen type does not use.
      weekDays: rec.type === 1 ? rec.weekDays
              : (rec.type >= 2 && rec.monthlyMode === 'weekday') ? rec.onWeekday : 0,
      weekOfMonth: (rec.type >= 2 && rec.monthlyMode === 'weekday') ? rec.weekOfMonth : 0,
      dayNumber: (rec.type >= 2 && rec.monthlyMode === 'day') ? rec.dayNumber : 0,
      month: rec.type === 3 ? rec.month : 0,
      range: rec.range === 2 ? 2 : 1,
      occurrenceCount: rec.range === 1 ? rec.occurrenceCount : 0,
      recurrenceEnd: rec.range === 2 ? rec.recurrenceEnd : null,
    };

    void runSave({
      formName: FORM,
      auditAction: row ? 'edit' : 'create',
      save: () => row
        ? api.put(`/api/v1/pre-login-settings/${cocOid}/schedules/${row.oid}`, body)
        : api.post(`/api/v1/pre-login-settings/${cocOid}/schedules`, body),
      onSaved,
    });
  };

  return (
    <>
      <AppDialog
        open={!recOpen}
        onClose={onCancel}
        title="Task Recurrance Setup"
        icon="ti-calendar-repeat"
        width={560}
        footer={<>
          <button className="ca-btn ca-btn-solid" onClick={submit}>
            <i className="ti ti-device-floppy" /> Save
          </button>
          <button className="ca-btn" onClick={onCancel}>Cancel</button>
        </>}
      >
        <div className="pl-add-grid" style={{ gridTemplateColumns: '110px 1fr' }}>
          <label>COC ID</label>
          <input readOnly value={info.cocId ?? ''} />

          <label>Client Name</label>
          {/* A read-only field like its neighbours — it was a textarea in the
              editable Note style, so it read as something to type into. */}
          <input readOnly value={info.clientName ?? ''} title={info.clientName ?? undefined} />

          <label>Client Address</label>
          <input readOnly value={info.clientAddress ?? ''} />

          <label>Project Code</label>
          <input readOnly value={info.projectCode ?? ''} />

          <label>Project Name</label>
          <input readOnly value={info.projectName ?? ''} />

          <label className="req">Start On</label>
          <input type="date" value={startOn} onChange={e => setStartOn(e.target.value)} />

          <label className="req">Recurrence</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <input readOnly value={rec ? (recText || describe(rec)) : ''}
                   title={rec ? (recText || describe(rec)) : undefined} />
            <button className="ca-btn" style={{ minWidth: 30 }} onClick={() => setRecOpen(true)}
                    title="Set the recurrence">…</button>
          </div>

          <label>Priority</label>
          <select value={priority} onChange={e => setPriority(Number(e.target.value))}>
            <option value={0}>Primary</option>
            <option value={1}>Secondary</option>
            <option value={2}>Tertiary</option>
          </select>

          <label>Remark</label>
          <textarea rows={3} className="pl-add-note" style={{ marginTop: 0 }}
                    value={remark} onChange={e => setRemark(e.target.value)} />
        </div>
      </AppDialog>

      {recOpen && (
        <RecurrenceDialog
          value={rec ?? FRESH}
          onCancel={() => setRecOpen(false)}
          onOk={r => { setRec(r); setRecText(''); setRecOpen(false); }}
          onRemove={() => { setRec(null); setRecText(''); setRecOpen(false); }}
        />
      )}
    </>
  );
}

/** The recurrence editor behind the "…" button — laid out like the XAF popup
 *  (owner screenshot 2026-09-29): a small "Recurrence" window, frequency radios
 *  down the left with their rule beside them, the two End radios underneath, and
 *  OK · Cancel · Remove recurrence. Legacy customisations kept
 *  (TaskRecurranceSetupController.RecurrenceControl_Load): no "No end date",
 *  "Last" shown as "Fifth". */
function RecurrenceDialog({ value, onOk, onCancel, onRemove }: {
  value: Rec;
  onOk: (r: Rec) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const notify = useNotify();
  const [r, setR] = useState<Rec>(value);
  const set = (patch: Partial<Rec>) => setR(prev => ({ ...prev, ...patch }));

  const ok = () => {
    if (r.type === 1 && r.weekDays === 0) { notify.error('Select at least one day of the week.'); return; }
    if (r.range === 0) { notify.error('Choose how the recurrence ends: End after or End by.'); return; }
    if (r.range === 2 && !r.recurrenceEnd) { notify.error("'End by' date must not be empty."); return; }
    if (r.range === 1 && r.occurrenceCount < 1) { notify.error('Occurrences must be at least 1.'); return; }
    onOk(r);
  };

  return (
    <AppDialog
      open
      onClose={onCancel}
      title="Recurrence"
      icon="ti-repeat"
      width={470}
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={ok}>OK</button>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn" onClick={onRemove}>Remove recurrence</button>
      </>}
    >
      <div className="pl-rec" style={{ gridTemplateColumns: '100px minmax(0, 1fr)' }}>
        <div className="pl-rec-freq" role="radiogroup" aria-label="Repeat">
          {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((label, i) => (
            <label key={label}>
              <input type="radio" name="rtype" checked={r.type === i} onChange={() => set({ type: i })} /> {label}
            </label>
          ))}
        </div>

        <div className="pl-rec-rule">
          <div className="pl-line">
            {r.type === 0 && <input type="radio" checked readOnly aria-label="Every" />}
            Every
            <input type="number" min={1} className="pl-in pl-small"
                   value={r.periodicity} onChange={e => set({ periodicity: Number(e.target.value) })} />
            {['day(s)', 'week(s)', 'month(s)', 'year(s)'][r.type]}
          </div>

          {r.type === 1 && (
            <div className="pl-days">
              {WEEKDAYS.map(d => (
                <label key={d.flag}>
                  <input type="checkbox" checked={(r.weekDays & d.flag) !== 0}
                         onChange={e => set({ weekDays: e.target.checked ? r.weekDays | d.flag : r.weekDays & ~d.flag })} />
                  {d.label}
                </label>
              ))}
            </div>
          )}

          {r.type >= 2 && (
            <>
              {r.type === 3 && (
                <div className="pl-line">
                  Of
                  <select className="pl-in pl-medium" value={r.month} onChange={e => set({ month: Number(e.target.value) })}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              )}
              <div className="pl-line">
                <label><input type="radio" name="mmode" checked={r.monthlyMode === 'day'}
                               onChange={() => set({ monthlyMode: 'day' })} /> Day</label>
                <input type="number" min={1} max={31} className="pl-in pl-small"
                       disabled={r.monthlyMode !== 'day'}
                       value={r.dayNumber} onChange={e => set({ dayNumber: Number(e.target.value) })} />
              </div>
              <div className="pl-line">
                <label><input type="radio" name="mmode" checked={r.monthlyMode === 'weekday'}
                               onChange={() => set({ monthlyMode: 'weekday' })} /> The</label>
                <select className="pl-in pl-medium" disabled={r.monthlyMode !== 'weekday'}
                        value={r.weekOfMonth} onChange={e => set({ weekOfMonth: Number(e.target.value) })}>
                  {WEEK_OF_MONTH.map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
                </select>
                <select className="pl-in pl-medium" disabled={r.monthlyMode !== 'weekday'}
                        value={r.onWeekday} onChange={e => set({ onWeekday: Number(e.target.value) })}>
                  {WEEKDAYS.map(d => <option key={d.flag} value={d.flag}>{d.label}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pl-rec-ends">
        <div className="pl-line">
          <label><input type="radio" name="range" checked={r.range === 1} onChange={() => set({ range: 1 })} /> End after:</label>
          <input type="number" min={1} className="pl-in pl-small" disabled={r.range !== 1}
                 value={r.occurrenceCount} onChange={e => set({ occurrenceCount: Number(e.target.value) })} />
          occurrences
        </div>
        <div className="pl-line">
          <label><input type="radio" name="range" checked={r.range === 2} onChange={() => set({ range: 2 })} /> End by:</label>
          <input type="date" className="pl-in pl-medium" disabled={r.range !== 2}
                 value={r.recurrenceEnd} onChange={e => set({ recurrenceEnd: e.target.value })} />
        </div>
      </div>
    </AppDialog>
  );
}
